import type { ScanSubmitResult } from './scan';

/**
 * Pure mapping from a submission result to `EnrichedInfoSection.tsx`'s
 * displayed fields (R11/R18, U6 + U10 of the lead-scanner plans, ported
 * inline off the original `SubmitResponseModal.tsx` popup per a later
 * product simplification) — split out of the component itself (which pulls
 * in React, `@tanstack/react-query`'s types transitively via `../scan`, and
 * several Glaze subpath modules) so "poll result → fields populated" is a
 * zero-import, independently unit-testable function, the same way
 * `photo-intake.ts` is split from `PhotoIntake.tsx`.
 *
 * It carries the whole BRANCH decision too, not just its strings: `pending`
 * and `status` are the two discriminants the component renders from
 * (pending / success layout / failure layout / the `partial` advisory), so
 * the branch table is asserted here in a plain unit test instead of only
 * through a rendered component.
 */
export interface SubmitModalFields {
  leadLine: string;
  campaign: string;
  scannedBy: string;
  /** The run is still in flight — the modal shows skeletons, not em-dashes. */
  pending: boolean;
  /** The Clay function's own `Completion Status`; `null` while pending. */
  status: 'Complete' | 'partial' | 'failed' | null;
  crmUrl: string | null;
  /**
   * The GTME BOOKING link (the contract's `Final GTME Meeting Link`), which
   * both the copy button and the QR code encode (settled §17 decision — the
   * QR no longer encodes a self-link back into this app). Populated only when
   * routing actually resolved a GTME, so a `partial` run leaves it `null` and
   * the modal omits the Booking/Share rows entirely.
   */
  bookingUrl: string | null;
  routingDecision: string | null;
  gtmeName: string | null;
  /**
   * `Final Followup Explainer`, rendered as prose exactly as the function
   * wrote it. "Routed to ClayDR" lives INSIDE this sentence by explicit
   * decision and is never parsed out into its own field.
   */
  followupExplainer: string | null;
  /** Rep-facing reason for a `failed` run; `null` on every other arm. */
  errorMessage: string | null;
}

export function buildSubmitModalFields(
  scan: { name: string; company: string },
  result: ScanSubmitResult,
  campaignName: string,
  scannedByName: string,
): SubmitModalFields {
  const identity = {
    leadLine: [scan.name, scan.company].filter(Boolean).join(' · '),
    campaign: campaignName,
    scannedBy: scannedByName,
  };

  // The pending arm has no fields at all to map — it is `{pending: true}` and
  // nothing else (U9's `ScanSubmitResultSchema`), so everything the terminal
  // arms fill in is null here rather than absent, keeping one flat shape for
  // the component to read.
  if (result.pending) {
    return {
      ...identity,
      pending: true,
      status: null,
      crmUrl: null,
      bookingUrl: null,
      routingDecision: null,
      gtmeName: null,
      followupExplainer: null,
      errorMessage: null,
    };
  }

  return {
    ...identity,
    pending: false,
    status: result.status,
    crmUrl: result.crmUrl,
    bookingUrl: result.routing.bookingLink,
    routingDecision: result.routing.decision,
    gtmeName: result.routing.gtmeName,
    followupExplainer: result.routing.followupExplainer,
    errorMessage: result.error,
  };
}
